/**
 * Data Service with Caching
 * Provides cached data access for admin panel tabs
 */

import { getUsers as apiGetUsers } from './userService';
import { getCharities as apiGetCharities } from './charityService';
import { getPledges as apiGetPledges } from './pledgeService';
import { getCachedData, setCachedData, clearCachedData } from './cacheService';

// Cache keys for different data types
const CACHE_KEYS = {
  USERS: 'users',
  CHARITIES: 'charities',
  PLEDGES: 'pledges'
};

/**
 * Generic function to get data with caching
 * @param {string} cacheKey - Cache key to use
 * @param {Function} apiFetch - API function to call if cache miss
 * @param {boolean} forceRefresh - Whether to bypass cache
 * @returns {Promise<any>} - Data from cache or API
 */
const getDataWithCache = async (cacheKey, apiFetch, forceRefresh = false) => {
  try {
    // Check cache first unless force refresh is requested. A TTL miss
    // (handled inside getCachedData) returns null here, so we refetch.
    if (!forceRefresh) {
      const cachedData = getCachedData(cacheKey);
      if (cachedData && cachedData.data) {
        return cachedData.data;
      }
    }

    // Cache miss, expired, or force refresh - fetch from API
    const freshData = await apiFetch();

    // Cache the fresh data
    setCachedData(cacheKey, freshData);

    return freshData;
  } catch (error) {
    console.error(`Error in getDataWithCache for ${cacheKey}:`, error);

    // Resilience fallback: if the API call fails, fall back to cached data
    // EVEN IF it is past its TTL, but only here, and log loudly that the data
    // may be stale. This keeps the panel usable during a backend blip without
    // silently presenting expired data as if it were fresh on the happy path.
    const staleCache = getCachedData(cacheKey, { ignoreTTL: true });
    if (staleCache && staleCache.data) {
      console.warn(
        `[dataService] API failed for "${cacheKey}"; serving STALE cached data ` +
        `from ${staleCache.timestamp ? new Date(staleCache.timestamp).toISOString() : 'unknown time'}.`
      );
      return staleCache.data;
    }

    // No fallback available - surface the error.
    throw error;
  }
};

/**
 * Get users data with caching
 * @param {boolean} forceRefresh - Whether to bypass cache and fetch fresh data
 * @returns {Promise<Object>} - Users data object
 */
export const getUsers = (forceRefresh = false) => {
  return getDataWithCache(CACHE_KEYS.USERS, apiGetUsers, forceRefresh);
};

/**
 * Get charities data with caching
 * @param {boolean} forceRefresh - Whether to bypass cache and fetch fresh data
 * @returns {Promise<Object>} - Charities data object
 */
export const getCharities = (forceRefresh = false) => {
  return getDataWithCache(CACHE_KEYS.CHARITIES, apiGetCharities, forceRefresh);
};

/**
 * Get pledges data with caching
 * @param {boolean} forceRefresh - Whether to bypass cache and fetch fresh data
 * @returns {Promise<Array|Object>} - Pledges data (array or object depending on API response)
 */
export const getPledges = (forceRefresh = false) => {
  return getDataWithCache(CACHE_KEYS.PLEDGES, apiGetPledges, forceRefresh);
};

/**
 * Refresh specific data type by clearing cache and fetching fresh data
 * @param {string} dataType - Type of data to refresh ('users', 'charities', 'pledges')
 * @returns {Promise<any>} - Fresh data
 */
export const refreshData = async (dataType) => {
  const cacheKey = CACHE_KEYS[dataType.toUpperCase()];
  if (!cacheKey) {
    throw new Error(`Invalid data type: ${dataType}`);
  }

  // Clear the cache for this data type
  clearCachedData(cacheKey);
  
  // Fetch fresh data
  switch (dataType.toLowerCase()) {
    case 'users':
      return getUsers(true);
    case 'charities':
      return getCharities(true);
    case 'pledges':
      return getPledges(true);
    default:
      throw new Error(`Unsupported data type: ${dataType}`);
  }
};

/**
 * Clear cache for specific data type
 * @param {string} dataType - Type of data to clear ('users', 'charities', 'pledges')
 * @returns {boolean} - Success status
 */
export const clearDataCache = (dataType) => {
  const cacheKey = CACHE_KEYS[dataType.toUpperCase()];
  if (!cacheKey) {
    console.error(`Invalid data type: ${dataType}`);
    return false;
  }
  
  return clearCachedData(cacheKey);
};

/**
 * Check if data is cached for a specific type
 * @param {string} dataType - Type of data to check ('users', 'charities', 'pledges')
 * @returns {boolean} - Whether data is cached
 */
export const isDataCached = (dataType) => {
  const cacheKey = CACHE_KEYS[dataType.toUpperCase()];
  if (!cacheKey) {
    return false;
  }
  
  const cachedData = getCachedData(cacheKey);
  return cachedData && cachedData.data;
};

/**
 * Get cache status for all data types
 * @returns {Object} - Cache status for each data type
 */
export const getCacheStatus = () => {
  return {
    users: isDataCached('users'),
    charities: isDataCached('charities'),
    pledges: isDataCached('pledges')
  };
};